import { createContext, FC, ReactNode, useContext, useEffect, useState, useMemo } from 'react';
import { useGetCourseHearingsQuery } from '@teachur/http-client';
import type { CourseHearingListItem } from '@teachur/http-client';
import { endOfDay, format, isValid, parse, startOfDay } from 'date-fns';
import {
  SEARCH_DEBOUNCE_MS,
  DEFAULT_HEARING_DATE_PRESET,
} from '../constants/courseHearings.constants';
import type {
  HearingStatusFilter,
  SearchMode,
  HearingDatePreset,
} from '../constants/courseHearings.constants';
import { getPresetDateRange } from '../utils/dateRange';

export type HearingSortKey = 'requestedAt' | 'startTime';
export type HearingSortDirection = 'asc' | 'desc' | null;

const DEFAULT_RANGE = getPresetDateRange(DEFAULT_HEARING_DATE_PRESET);
const DEFAULT_SORT_KEY: HearingSortKey = 'startTime';
const DEFAULT_SORT_DIRECTION: HearingSortDirection = 'desc';

interface CourseHearingsContextValue {
  calls: CourseHearingListItem[];
  isLoading: boolean;
  isFetchingNewParams: boolean;
  isError: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchMode: SearchMode;
  setSearchMode: (mode: SearchMode) => void;
  statusFilter: HearingStatusFilter;
  setStatusFilter: (status: HearingStatusFilter) => void;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  datePreset: HearingDatePreset | null;
  setDatePreset: (preset: HearingDatePreset) => void;
  sortKey: HearingSortKey | null;
  sortDirection: HearingSortDirection;
  setSort: (key: HearingSortKey, direction: HearingSortDirection) => void;
  page: number;
  setPage: (page: number) => void;
  total: number;
  selectedCall: CourseHearingListItem | null;
  setSelectedCall: (call: CourseHearingListItem | null) => void;
}

const CourseHearingsContext = createContext<CourseHearingsContextValue | undefined>(undefined);

interface CourseHearingsProviderProps {
  children: ReactNode;
}

export const CourseHearingsProvider: FC<CourseHearingsProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('course');
  const [statusFilter, setStatusFilter] = useState<HearingStatusFilter>('');
  const [dateFrom, setDateFrom] = useState(DEFAULT_RANGE.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_RANGE.dateTo);
  const [datePreset, setDatePreset] = useState<HearingDatePreset | null>(
    DEFAULT_HEARING_DATE_PRESET
  );
  const [sortKey, setSortKey] = useState<HearingSortKey | null>(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<HearingSortDirection>(DEFAULT_SORT_DIRECTION);
  const [page, setPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState<CourseHearingListItem | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const onSetStatusFilter = (status: HearingStatusFilter) => {
    setStatusFilter(status);
    setPage(1);
  };

  const onSetDateFrom = (date: string) => {
    setDateFrom(date);
    setDatePreset(null); // manual edit = custom range
    setPage(1);
  };

  const onSetDateTo = (date: string) => {
    setDateTo(date);
    setDatePreset(null); // manual edit = custom range
    setPage(1);
  };

  const onSetDatePreset = (preset: HearingDatePreset) => {
    const range = getPresetDateRange(preset);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setDatePreset(preset);
    setPage(1);
  };

  const onSetSearchMode = (mode: SearchMode) => {
    setSearchMode(mode);
    setPage(1);
  };

  const onSetSort = (key: HearingSortKey, direction: HearingSortDirection) => {
    setSortKey(direction === null ? null : key);
    setSortDirection(direction);
    setPage(1);
  };

  // Returns undefined for an unparseable date so an invalid value silently
  // drops the filter instead of throwing in format().
  const toBoundaryDateTime = (date: string, isEnd: boolean): string | undefined => {
    const parsed = parse(date, 'yyyy-MM-dd', new Date());
    if (!isValid(parsed)) return undefined;
    const bounded = isEnd ? endOfDay(parsed) : startOfDay(parsed);
    return format(bounded, "yyyy-MM-dd'T'HH:mm:ssXXX");
  };

  const isMissedFilter = statusFilter === 'missed';
  const apiDerivedStatus = isMissedFilter ? undefined : statusFilter;

  const queryDateFrom = useMemo(
    () => (dateFrom ? toBoundaryDateTime(dateFrom, false) : undefined),
    [dateFrom]
  );
  const queryDateTo = useMemo(
    () => (dateTo ? toBoundaryDateTime(dateTo, true) : undefined),
    [dateTo]
  );

  const { data, isLoading, isError, isPreviousData } = useGetCourseHearingsQuery(
    {
      ...(apiDerivedStatus ? { derivedStatus: apiDerivedStatus } : {}),
      ...(isMissedFilter ? { missed: true } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim(), searchBy: searchMode } : {}),
      ...(queryDateFrom ? { dateFrom: queryDateFrom } : {}),
      ...(queryDateTo ? { dateTo: queryDateTo } : {}),
      ...(sortKey && sortDirection ? { sortBy: sortKey, sortDir: sortDirection } : {}),
      page,
    },
    { keepPreviousData: true, staleTime: Infinity }
  );

  // keepPreviousData retains the prior response while a changed query loads.
  // Never surface those retained rows as results for the new filter/sort/page:
  // treat them as pending so the table shows no stale, unselectable rows.
  const isRefetchingNewParams = isPreviousData;
  const calls = isRefetchingNewParams ? [] : (data?.data ?? []);
  const total = isRefetchingNewParams ? 0 : (data?.total ?? 0);

  const value: CourseHearingsContextValue = {
    calls,
    isLoading,
    isFetchingNewParams: isRefetchingNewParams,
    isError,
    searchQuery,
    setSearchQuery,
    searchMode,
    setSearchMode: onSetSearchMode,
    statusFilter,
    setStatusFilter: onSetStatusFilter,
    dateFrom,
    setDateFrom: onSetDateFrom,
    dateTo,
    setDateTo: onSetDateTo,
    datePreset,
    setDatePreset: onSetDatePreset,
    sortKey,
    sortDirection,
    setSort: onSetSort,
    page,
    setPage,
    total,
    selectedCall,
    setSelectedCall,
  };

  return <CourseHearingsContext.Provider value={value}>{children}</CourseHearingsContext.Provider>;
};

export const useCourseHearingsContext = (): CourseHearingsContextValue => {
  const context = useContext(CourseHearingsContext);

  if (!context) {
    throw new Error('useCourseHearingsContext must be used within CourseHearingsProvider');
  }

  return context;
};
